import React from "react";
import {useSelector} from "react-redux";
import TableWrapper from "../Table/tableWrapper";
import {useFetchMonthlyBillingsQuery} from "../../Apis/billingApi";
import {useFetchCardsQuery} from "../../Apis/cardApi";
import {useFetchAchQuery} from "../../Apis/achApi";
import {ownPermissionsSelector} from "../../Reducers/ownRoleReducer";
import {enterpriseDetailsSelector} from "../../Reducers/enterpriseDetailsReducer";
import {DATE_FORMATS, formatDate} from "../../Util/dateUtils";
import styles from "./billingPageWrapper.module.css";

const BillsSectionHeading = () => (
    <div className={styles.sectionHeader}>
        <span className="sub-title">Bills</span>
        <p className={styles.sectionSubheading}>
            Invoices charged to your enterprise for covered companies. Totals may differ from Usage.
        </p>
    </div>
);

const BILLS_TABLE_HEADERS = [
    "Billing Period",
    "Billed Accounts",
    "Amount",
    "Payment Method",
    "Status",
    "Charged On",
];

const formatLabel = (value) => {
    if (!value) return "—";

    return value.charAt(0) + value.slice(1).toLowerCase();
};

const getStatus = (billing) => {
    if (billing.getIsPaid()) return "Paid";

    return formatLabel(billing.getChargingStatus());
};

const getPaymentMethod = (billing, cardsData, achData, currentAdminId) => {
    if (billing.getPaymentSource() === "MANUAL") return "External billing";
    if (billing.getPaymentSource() !== "STRIPE") return "Payment details unavailable";

    const billingAdminId = billing.getAdministratorId();
    if (currentAdminId != null && billingAdminId != null && String(currentAdminId) !== String(billingAdminId)) {
        return "Payment details unavailable";
    }

    if (billing.getKeyType() === "CARD") {
        const card = cardsData?.getCards()?.find(
            (item) => String(item.getId()) === String(billing.getKey())
        );
        return card?.getDisplayText() ?? "Payment details unavailable";
    }

    if (billing.getKeyType() === "US_BANK_ACCOUNT") {
        const bank = achData?.getBanks()?.find(
            (item) => String(item.getId()) === String(billing.getKey())
        );
        return bank?.getDisplayText() ?? "Payment details unavailable";
    }

    return "Payment details unavailable";
};

const getBillsTable = (monthlyBillings, cardsData, achData, currentAdminId) => {
    if (!Array.isArray(monthlyBillings)) {
        return [];
    }

    return monthlyBillings
        .filter((billing) => billing && typeof billing.getStartDate === "function")
        .map((billing) => [
    formatDate(billing.getStartDate(), DATE_FORMATS.MMM_YYYY),
    billing.getCompanies().length,
    billing.getUsageAmount().toFixed(2),
    getPaymentMethod(billing, cardsData, achData, currentAdminId),
    getStatus(billing),
    billing.getChargedAt()
        ? formatDate(billing.getChargedAt(), DATE_FORMATS.MMM_DDc_YYYY)
        : "—",
    ]);
};

const BillsTableSection = () => {
    const ownPermissions = useSelector(ownPermissionsSelector);
    const enterpriseDetails = useSelector(enterpriseDetailsSelector);
    const canReadBilling = ownPermissions?.canReadBilling();
    const {
        data: monthlyBillings,
        isLoading,
        isFetching,
        isError,
        isUninitialized,
    } = useFetchMonthlyBillingsQuery(undefined, {
        skip: !canReadBilling,
    });
    const {data: cardsData} = useFetchCardsQuery(undefined, {skip: !canReadBilling});
    const {data: achData} = useFetchAchQuery(undefined, {skip: !canReadBilling});

    const billingsNotReady =
        isUninitialized
        || isLoading
        || (isFetching && monthlyBillings === undefined);

    let errorText = "No bills available";
    if (billingsNotReady) errorText = "Loading bills...";
    if (isError) errorText = "Bills could not be loaded";

    const tableBodyArr =
        billingsNotReady || isError || monthlyBillings === undefined
            ? []
            : getBillsTable(monthlyBillings, cardsData, achData, enterpriseDetails?.id);

    return (
        <div className="row ps-0 min-width-md-parent">
            <BillsSectionHeading/>
            <TableWrapper
                tableHeaderArr={BILLS_TABLE_HEADERS}
                tableBodyArr={tableBodyArr}
                errorText={errorText}
            />
        </div>
    );
};

export default BillsTableSection;
